import React, { useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useData } from "../context/DataContext";
import { resolveImageUrl } from "../admin/ImageUpload";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Loader2, CheckCircle2 } from "lucide-react";

const ParentResetPassword = () => {
  const { settings } = useData();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const logo = resolveImageUrl(settings?.logo);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!token) { toast.error("Lien invalide."); return; }
    if (password.length < 6) { toast.error("Le mot de passe doit contenir au moins 6 caractères."); return; }
    if (password !== confirm) { toast.error("Les deux mots de passe ne correspondent pas."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/parent/auth/reset-password", { token, newPassword: password });
      toast.success(data?.message || "Mot de passe mis à jour.");
      setDone(true);
      setTimeout(() => navigate("/espace-famille/login", { replace: true }), 1800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur, réessayez.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-cream" data-testid="parent-reset-page">
      <div className="py-5 px-6">
        <Link to="/espace-famille/login" className="inline-flex items-center gap-1 text-stone-600 hover:text-deep-rose text-sm">
          <ArrowLeft size={14} /> Retour à la connexion
        </Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-12">
        <div className="max-w-md w-full bg-white rounded-3xl shadow-xl p-8 sm:p-10">
          <div className="text-center mb-8">
            {logo ? (
              <img src={logo} alt="L'Élan Danse" className="h-20 mx-auto object-contain" />
            ) : (
              <div className="flex flex-col leading-none items-center">
                <span className="font-script text-5xl text-deep-rose">L&apos;élan</span>
                <span className="font-script text-4xl text-deep-rose -mt-1">Danse</span>
              </div>
            )}
            <p className="mt-3 text-xs uppercase tracking-widest text-stone-500">Nouveau mot de passe</p>
          </div>

          {!token && (
            <p className="text-center text-sm text-stone-600" data-testid="parent-reset-no-token">
              Lien invalide. Merci de refaire une demande depuis la page « Mot de passe oublié ».
            </p>
          )}

          {token && done && (
            <div className="text-center space-y-4" data-testid="parent-reset-done">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-blush-light text-deep-rose">
                <CheckCircle2 size={24} />
              </div>
              <p className="text-stone-700 text-sm">Mot de passe mis à jour. Redirection…</p>
            </div>
          )}

          {token && !done && (
            <form onSubmit={onSubmit} className="space-y-5" data-testid="parent-reset-form">
              <div>
                <label className="block text-sm uppercase tracking-widest text-stone-500 mb-2">Nouveau mot de passe</label>
                <input
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  type="password"
                  name="new-password"
                  autoComplete="new-password"
                  required
                  minLength={6}
                  data-testid="parent-reset-password"
                  className="w-full bg-cream rounded-xl border border-blush-light px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blush"
                />
                <div className="text-xs text-stone-500 mt-1">Minimum 6 caractères.</div>
              </div>
              <div>
                <label className="block text-sm uppercase tracking-widest text-stone-500 mb-2">Confirmez</label>
                <input
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  type="password"
                  name="confirm-password"
                  autoComplete="new-password"
                  required
                  minLength={6}
                  data-testid="parent-reset-confirm"
                  className="w-full bg-cream rounded-xl border border-blush-light px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blush"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="parent-reset-submit"
                className="w-full bg-coral text-white py-3 rounded-full font-serif-display tracking-widest uppercase text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {loading ? <Loader2 className="animate-spin" size={16} /> : <KeyRound size={16} />}
                {loading ? "Mise à jour..." : "Mettre à jour"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ParentResetPassword;
